import React, { useContext } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'react-router-dom';

import useAvailableLanguages from 'UI/customHooks/useAvailableLanguages';
import useConfig from 'UI/customHooks/useConfig';
import { clientLogger, navigateInternal } from 'UI/lib';

import { AUTH_ERROR_CODE_QUERY_PARAM, AUTH_ERROR_REASON_QUERY_PARAM } from '../../auth/constants';
import { getBrowserUserCredentials, logoutUser } from '../../auth/userCredentials';
import getApolloClient from '../../getApolloClient';
import { USER_PROFILE_UPDATE } from '../mutations/user';
import { GET_VIEWER } from '../queries/viewer';
import i18next from 'i18next';
import get from 'lodash/get';
import PropTypes from 'prop-types';

const INITIAL_STATE = {
  currentUser: null,
  loginError: null,
  isUserBeingLoaded: false,
};

export const UserContext = React.createContext();

class UserProviderComponent extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      ...INITIAL_STATE,
    };
  }

  componentDidMount() {
    const { currentUser } = this.state;

    if (!currentUser) {
      try {
        this.getCurrentUser();
      } catch (error) {
        clientLogger.error('Error while resolving user', error);
      }
    }
  }

  getCurrentUser = () => {
    const config = useConfig();
    const { palyxToken } = getBrowserUserCredentials();
    const availableLanguages = useAvailableLanguages();
    const defaultLang = availableLanguages.find(language => language.isDefault).languageCode;
    const activeLanguages = config?.cms?.features?.localization?.languages;

    if (!palyxToken) {
      return;
    }

    return new Promise(async (resolve, reject) => {
      this.setState({ isUserBeingLoaded: true });
      try {
        const { data, error } = await getApolloClient().query({
          fetchPolicy: 'no-cache',
          query: GET_VIEWER,
        });

        if (error) {
          throw error;
        }
        const { viewer: currentUser } = data;

        if (currentUser?.lastSelectedLanguage) {
          if (!activeLanguages[currentUser.lastSelectedLanguage]?.isActive) {
            await this.changeLanguage(defaultLang);
          } else {
            await this.changeLanguage(currentUser?.lastSelectedLanguage || defaultLang);
          }
        }

        if (!currentUser) {
          throw new Error('Failed to load viewer');
        }

        this.setState(
          {
            currentUser,
            isUserBeingLoaded: false,
          },
          () => resolve(currentUser)
        );
      } catch (error) {
        this.setState({ isUserBeingLoaded: false }, () => {
          reject(error);
        });
      }
    }).catch(error => {
      clientLogger.error(error, 'Error while resolving user');
      navigateInternal(
        `/?${AUTH_ERROR_CODE_QUERY_PARAM}=500&${AUTH_ERROR_REASON_QUERY_PARAM}=Not possible to fetch viewer data`
      );
      return;
    });
  };

  changeLanguage(language) {
    return i18next.changeLanguage(language, async err => {
      if (err) {
        clientLogger.error('something went wrong loading', err);
      }
    });
  }

  logout = async () => {
    await logoutUser();
  };

  updateProfile = async options => {
    const { currentUser } = this.state;
    const handledOptions = options;

    const optimisticData = {
      ...currentUser,
    };

    Object.keys(options).forEach(field => {
      optimisticData.profile[field] = options[field];
    });

    return getApolloClient().mutate({
      mutation: USER_PROFILE_UPDATE,
      variables: {
        record: {
          ...handledOptions,
          _id: currentUser._id,
        },
      },
      optimisticResponse: {
        __typename: 'Mutation',
        userProfileUpdate: {
          __typename: 'UserUpdateProfilePayload',
          recordId: currentUser._id,
          record: {
            __typename: 'User',
            ...optimisticData,
          },
        },
      },
      update: () => {
        this.getCurrentUser();
      },
    });
  };

  render() {
    const { state } = this;
    const { children } = this.props;

    return (
      <UserContext.Provider
        value={{
          state,
          logout: this.logout,
          updateProfile: this.updateProfile,
          getCurrentUser: this.getCurrentUser,
        }}
      >
        {children}
      </UserContext.Provider>
    );
  }
}

export const withUser = Children => {
  const WrappedComponent = props => (
    <UserContext.Consumer>{data => <Children {...props} user={data} />}</UserContext.Consumer>
  );

  return WrappedComponent;
};

UserProviderComponent.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useUserContext = () => useContext(UserContext);

export const useUserId = () => {
  const userInfo = useContext(UserContext);
  return get(userInfo, 'state.currentUser._id');
};

export const UserProvider = withTranslation('common')(withRouter(UserProviderComponent));
